import { Controller } from '@hotwired/stimulus';
import { patchOrder } from './tabulator_reorder/patch_order';

/**
 * Persists the manual row order of a `movable_rows:` Tabulator table, for both
 * ways a row can move: a drag, and per-row up/down buttons.
 *
 * Wrap the `UI::TabulatorTableComponent` render in this controller — it needs
 * both the hidden source `<table>` and the rendered table inside its element.
 *
 * ## What the screen provides
 *
 *   value  `sort-url`      PATCH endpoint for the whole order (required)
 *   value  `groups-param`  payload key holding the grouped order ("categories")
 *   value  `announcement-template`  live-region copy with `%{position}` and
 *                          `%{total}` placeholders, substituted here
 *   target `feedback`      shown (`d-none` removed) when a save fails
 *   target `move-controls` a row's up/down button container; its connection is
 *                          what refreshes the buttons' disabled state
 *   target `announcement`  a `visually-hidden` `aria-live="polite"` element; a
 *                          button move writes the new position into it
 *
 * ## DOM contract (all wrapper conventions, no screen vocabulary)
 *
 *   source rows     `[data-row-id]` inside `tbody[data-row-group]`
 *   rendered rows   `.tabulator-row[data-row-id]` (surfaced by the wrapper)
 *   move buttons    two `button[aria-disabled]` per row, up first then down
 *   position text   `.amba-tabulator-row-position` (optional, rewritten on save)
 *
 * ## Why the source table is the source of truth
 *
 * The wrapper reads its data from the hidden source `<table>`, and every
 * mutation stream swaps one group's `<tbody>` there. So a successful reorder
 * reconciles that table — re-parenting the moved `<tr>` and rewriting its
 * `data-row-group` — or the next `tabulator-table:sync-group` would delete the
 * row it just moved. A failed reorder leaves it untouched, which is why
 * re-reading it is also the revert.
 */
export default class extends Controller {
  static targets = ['feedback', 'moveControls', 'announcement'];
  static values = {
    sortUrl: String,
    // Live-region copy, with `%{position}` / `%{total}` substituted here. A
    // template rather than finished text because the numbers are only known at
    // move time, and the words have to stay in the locale file.
    announcementTemplate: String,
    // The endpoint's param name for the grouped order. `sortable_settings` is
    // the app-wide envelope (see the guide-category sort endpoint); the key
    // inside it is per-endpoint.
    groupsParam: { type: String, default: 'categories' },
  };

  connect() {
    this.onRowMoved = this.onRowMoved.bind(this);
    this.element.addEventListener('tabulator-table:row-moved', this.onRowMoved);
  }

  disconnect() {
    this.element.removeEventListener(
      'tabulator-table:row-moved',
      this.onRowMoved
    );
  }

  // Tabulator rebuilds a cell's HTML on every re-render, so a fresh set of
  // buttons connects on first render and after every sync, drag and reorder.
  // That is where their disabled state is kept honest.
  moveControlsTargetConnected() {
    this.refreshButtons();
  }

  // The table emits the complete new grouped order after a drag; it has already
  // moved the rendered rows itself, so only the source table needs reconciling.
  onRowMoved(event) {
    const groupedOrder = event.detail?.groupedOrder;
    if (groupedOrder) this.persist(groupedOrder);
  }

  // MoveRows binds its drag mousedown to the whole `rowHandle` cell, which the
  // buttons sit inside. Without this a press held past its 150ms `checkPeriod`
  // starts a drag under the click.
  blockDrag(event) {
    event.stopPropagation();
  }

  moveUp(event) {
    this.step(event, -1);
  }

  moveDown(event) {
    this.step(event, 1);
  }

  // Step a row one place within its own group. Never across groups: the ends of
  // a group are disabled, and the order sent only ever has two ids swapped
  // inside one bucket.
  step(event, delta) {
    const button = event.currentTarget;
    if (button.getAttribute('aria-disabled') === 'true') return;

    const rowId = button.closest('[data-row-id]')?.dataset.rowId;
    if (!rowId) return;

    // The group comes from the source row's `<tbody>`, not the clicked row: a
    // rendered row's group is written once from row data, while the source row
    // is re-parented by every successful reorder.
    const order = this.groupedOrder();
    const group = this.sourceRow(rowId)?.closest('tbody[data-row-group]')
      ?.dataset.rowGroup;
    const ids = order[group];
    if (!ids) return;

    const from = ids.indexOf(rowId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;

    ids.splice(to, 0, ...ids.splice(from, 1));
    // Unlike a drag, a button press moved nothing on screen, so the rendered
    // table has to be re-read from the reconciled source once the server agrees.
    // A button press is also the only reorder a screen-reader user can make, so
    // it is the one worth announcing — a drag is already visual and its mover is
    // watching it.
    this.persist(order, {
      resync: true,
      announce: { position: to + 1, total: ids.length },
    });
  }

  // The complete grouped order, read from the source table — which every
  // successful reorder reconciles, so it is the persisted order.
  groupedOrder() {
    const order = {};
    this.element.querySelectorAll('tbody[data-row-group]').forEach((body) => {
      order[body.dataset.rowGroup] = Array.from(
        body.querySelectorAll('[data-row-id]')
      ).map((row) => row.dataset.rowId);
    });
    return order;
  }

  // Only the ends of each group are barred from moving further, and which rows
  // those are changes with every reorder, drag, create and delete.
  refreshButtons() {
    Object.values(this.groupedOrder()).forEach((ids) => {
      ids.forEach((rowId, index) => {
        const first = index === 0;
        const last = index === ids.length - 1;
        // Both copies: the rendered row the user clicks, and the source row
        // whose HTML Tabulator re-reads when it rebuilds the cell.
        [this.renderedRow(rowId), this.sourceRow(rowId)].forEach((row) => {
          const buttons = row?.querySelectorAll('button[aria-disabled]');
          if (!buttons || buttons.length < 2) return;
          buttons[0].setAttribute('aria-disabled', String(first));
          buttons[1].setAttribute('aria-disabled', String(last));
        });
      });
    });
  }

  sourceRow(rowId) {
    return this.element.querySelector(
      `tbody[data-row-group] [data-row-id='${rowId}']`
    );
  }

  renderedRow(rowId) {
    return this.element.querySelector(`.tabulator-row[data-row-id='${rowId}']`);
  }

  async persist(groupedOrder, { resync = false, announce = null } = {}) {
    const ok = await patchOrder(this.sortUrlValue, {
      sortable_settings: { [this.groupsParamValue]: groupedOrder },
    });

    if (ok) {
      this.hideFeedback();
      this.reconcile(groupedOrder);
      if (resync) this.resyncTable();
      if (announce) this.announce(announce);
    } else {
      // Nothing was written and the source table never moved, so re-reading it
      // puts the rendered table back to the persisted order.
      this.resyncTable();
      this.showFeedback();
    }
  }

  // Ask the table to re-read the source `<table>`. The listener is on the
  // table's own root, which is a child of ours, so target it directly.
  resyncTable() {
    this.element
      .querySelector("[data-controller~='tabulator-table']")
      ?.dispatchEvent(new CustomEvent('tabulator-table:resync-rows'));
  }

  // Clear before setting so the same message is re-announced when a row is moved
  // twice to the same position (see stimulus/accessibility.md).
  announce({ position, total }) {
    if (!this.hasAnnouncementTarget || !this.announcementTemplateValue) return;

    this.announcementTarget.textContent = '';
    this.announcementTarget.textContent = this.announcementTemplateValue
      .replace('%{position}', position)
      .replace('%{total}', total);
  }

  showFeedback() {
    if (this.hasFeedbackTarget) this.feedbackTarget.classList.remove('d-none');
  }

  hideFeedback() {
    if (this.hasFeedbackTarget) this.feedbackTarget.classList.add('d-none');
  }

  // Re-parent each moved source row into its group's `<tbody>`, in the order the
  // server just wrote, and rewrite the position text the server rendered — on
  // the source row and on the rendered one.
  reconcile(groupedOrder) {
    Object.entries(groupedOrder).forEach(([group, rowIds]) => {
      const body = this.element.querySelector(
        `tbody[data-row-group='${group}']`
      );
      if (!body) return;

      rowIds.forEach((rowId, index) => {
        const position = String(index + 1);
        const row = this.sourceRow(rowId);
        if (row) {
          row.dataset.rowGroup = group;
          this.setPosition(row, position);
          // Appending in payload order rebuilds the whole `<tbody>` in order —
          // every row in the group is named, so nothing is left behind.
          body.appendChild(row);
        }
        const rendered = this.renderedRow(rowId);
        this.setPosition(rendered, position);
        // Keep the rendered row's group truthful too: it is written once from
        // row data, so after a cross-group move it would still claim the old one.
        if (rendered) rendered.dataset.rowGroup = group;
      });
    });

    this.refreshButtons();
  }

  setPosition(row, position) {
    const cell = row?.querySelector('.amba-tabulator-row-position');
    if (cell) cell.textContent = position;
  }
}
